import {
  Component,
  Element,
  Host,
  Listen,
  Prop,
  State,
  Watch,
  forceUpdate,
  h,
} from '@stencil/core';
import { IntergatorConnector } from '../../../common/intergator-connector';
import store from '../store';

interface UiSuggestion {
  text: string;
  url: string;
  id: number;
  fondsname?: string;
  isin?: string;
  wkn?: string;
}

interface UiSuggestionGroup {
  headline: string;
  hits: UiSuggestion[];
}

@Component({
  tag: 'ui-download-search-autosuggest',
  styleUrl: 'ui-download-search-autosuggest.scss',
  shadow: false,
})
export class UiDownloadSearchAutosuggest {
  @Element() el: HTMLElement;

  @Prop() placeholder: string;

  @Prop() minLength = 0;

  @Prop() suggestionsApi = '';

  @Prop() apiKey: string;

  @Prop() searchType: string;

  @Prop() omitClauses: string;

  @Prop() omitDuplicates: string;

  @State() suggestionGroups: UiSuggestionGroup[] = [];

  @State() focusedSuggestion: number = null;

  @State() highlightedSuggestion: number = null;

  @State() searchValue: string;

  @Prop() inputInFocus = true;

  @State() suggestionsApiConnector: IntergatorConnector;

  searchFiredBySuggestion = false;

  // Limit number for suggestions in dropdown
  numberOfSuggestions = 12;

  // listening for a click on search icon in input filed
  @Listen('inputIconClicked')
  reactOnSearchIconClicked() {
    this.onSubmit();
  }

  @Listen('uiInputClear')
  reactOnClearClicked() {
    this.clearInput();
  }

  componentWillLoad() {
    this.setApiConnectors();

    store.onChange('fundsMode', () => {
      this.searchValue = null;
    });
  }

  componentDidLoad() {
    window.ui?.services?.i18n?.registerI18nUpdateCallback(() => {
      forceUpdate(this);
    });
  }

  @Watch('suggestionsApi')
  @Watch('apiKey')
  apiLoaded() {
    this.setApiConnectors();
  }

  setApiConnectors() {
    if (this.suggestionsApi && this.apiKey) {
      this.suggestionsApiConnector = new IntergatorConnector({
        url: this.suggestionsApi,
        key: this.apiKey,
      });
    }
  }

  @Watch('searchValue')
  async triggerSearch() {
    if (
      this.suggestionsApi &&
      ((this.searchValue?.length > this.minLength &&
        !this.searchFiredBySuggestion) ||
        store.state.fundsMode)
    ) {
      const request = await this.suggestionsApiConnector.fetchSuggestions({
        keyword:
          this.searchValue || store.state.fundsMode
            ? this.searchValue || ''
            : '',
        filters: IntergatorConnector.getDownloadSearchSuggestionFilters(
          store.state.fundsMode
        ),
      });

      if (request.ok) {
        const suggestionGroups = (await request.clone().json()).results;
        const limitedSuggestions =
          IntergatorConnector.returnLimitedNumberOfSuggestions(
            suggestionGroups,
            this.numberOfSuggestions
          );
        this.suggestionGroups =
          IntergatorConnector.convertSuggestionGroups(limitedSuggestions);
        this.focusedSuggestion = null;
        return;
      }
    }
    this.suggestionGroups = [];
  }

  @Listen('focus', { capture: true })
  handleClick() {
    this.triggerSearch();
  }

  keyCodes = {
    enter: 'Enter',
    up: 'ArrowUp',
    upIe11: 'Up',
    down: 'ArrowDown',
    downIe11: 'Down',
    esc: 'Escape',
  };

  get allSuggestions(): UiSuggestion[] {
    return this.suggestionGroups.reduce(
      (aggr: UiSuggestion[], group) => [...aggr, ...group.hits],
      []
    );
  }

  get activeSuggestion(): UiSuggestion {
    return this.allSuggestions.find(
      (suggestion) => suggestion.id === this.focusedSuggestion
    );
  }

  onChange(event: CustomEvent & KeyboardEvent) {
    if (this.searchFiredBySuggestion) {
      this.searchFiredBySuggestion = false;
    }
    this.searchValue = event.detail.value;
    if (this.highlightedSuggestion) {
      this.highlightedSuggestion = null;
    }
  }

  onKeyUp(event: KeyboardEvent) {
    switch (event.key) {
      case this.keyCodes.enter:
        this.onSubmit();
        break;
      case this.keyCodes.down:
        this.onArrowDown();
        break;
      case this.keyCodes.downIe11:
        this.onArrowDown();
        break;
      case this.keyCodes.up:
        this.onArrowUp();
        break;
      case this.keyCodes.upIe11:
        this.onArrowDown();
        break;
      case this.keyCodes.esc:
        this.clearInput();
        break;
      default:
        break;
    }
  }

  onArrowUp() {
    if (this.highlightedSuggestion !== 0) {
      this.highlightedSuggestion -= 1;
      this.focusedSuggestion = this.highlightedSuggestion;
    }
  }

  onArrowDown() {
    if (this.highlightedSuggestion === null) {
      this.highlightedSuggestion = 0;
      this.focusedSuggestion = this.highlightedSuggestion;
      return;
    }

    if (this.highlightedSuggestion < this.allSuggestions.length - 1) {
      this.highlightedSuggestion += 1;
      this.focusedSuggestion = this.highlightedSuggestion;
    }
  }

  clearInput() {
    store.state.searchValue = '';
  }

  fireSearch(query: string) {
    this.suggestionGroups = [];
    store.state.searchValue = query;
  }

  onSubmit() {
    if (this.activeSuggestion) {
      this.onSuggestionClick(this.activeSuggestion);
    } else {
      this.fireSearch(this.searchValue);
      store.state.tracking.trackAction('DownloadSearchManualSearch', 'Suche');
    }
  }

  onSuggestionClick(suggestion: UiSuggestion) {
    if (store.state.fundsMode) {
      store.state.tracking.trackAction(
        'DownloadSearchSuggestSearch',
        suggestion.fondsname
      );
      this.searchFiredBySuggestion = true;
      if (
        !store.state.selectedFunds.find((fund) => fund.isin === suggestion.isin)
      ) {
        store.state.selectedFunds = [...store.state.selectedFunds, suggestion];
      }
      this.searchValue = null;
      // eslint-disable-next-line compat/compat
      this.fireSearch(null);
      return;
    }
    store.state.tracking.trackAction(
      'DownloadSearchSuggestSearch',
      suggestion.text
    );
    this.searchFiredBySuggestion = true;
    this.searchValue = suggestion.text;
    // eslint-disable-next-line compat/compat
    this.fireSearch(suggestion.text);
  }

  focusSuggestion(suggestionId: number) {
    this.highlightedSuggestion = suggestionId;
    this.focusedSuggestion = suggestionId;
  }

  highlightSuggestion(suggestionId: number) {
    this.highlightedSuggestion = suggestionId;
  }

  renderSuggestionContent = (suggestion: UiSuggestion) => {
    const highlightString = (string) =>
      string?.replace(
        new RegExp(`(${this.searchValue})`, 'gi'),
        '<span class="highlight"><b>$1</b></span>'
      );
    const additionalContent = [`ISIN: ${highlightString(suggestion?.isin)}`];
    // AT entries don't have WKN therefore should be hidden
    if (suggestion?.wkn)
      additionalContent.push(`WKN: ${highlightString(suggestion?.wkn)}`);
    if (store.state.fundsMode) {
      return `${highlightString(suggestion?.fondsname)},
        <span style="color: var(--ui-suggestion-additional-content);">
          ${additionalContent.join(', ')}
        </span>`;
    }
    return highlightString(suggestion?.text);
  };

  renderSuggestion = (suggestion: UiSuggestion) => {
    if (
      store.state.fundsMode &&
      store.state.selectedFunds.find(
        (selectedFund) => selectedFund.isin === suggestion.isin
      )
    ) {
      return null;
    }
    return (
      <ui-suggestion
        class={{ focus: suggestion.id === this.highlightedSuggestion }}
        key={suggestion.id}
        onClick={() => this.onSuggestionClick(suggestion)}
        innerHTML={this.renderSuggestionContent(suggestion)}
        onMouseEnter={() => this.highlightSuggestion(suggestion.id)}
        onFocus={() => this.focusSuggestion(suggestion.id)}
        tabindex="0"
        role="button"
      ></ui-suggestion>
    );
  };

  render() {
    return (
      <Host>
        <ui-autosuggest
          onUiAutosuggestChange={this.onChange.bind(this)}
          onKeyUp={this.onKeyUp.bind(this)}
          value={this.searchValue}
          placeholder={this.placeholder}
          icon={store.state.fundsMode ? 'chevron-bottom' : 'magnifying-glass'}
          inputInFocus={store.state.fundsMode ? false : this.inputInFocus}
          search-value={store.state.searchValue}
          setSearchValueAutomatically={false}
          showOnFocus={store.state.fundsMode}
        >
          {this.suggestionGroups.map((suggestionGroup, i) => (
            <ui-suggestion-group
              key={suggestionGroup.headline + i}
              title={suggestionGroup.headline}
            >
              {suggestionGroup.hits?.map((suggestion) =>
                this.renderSuggestion(suggestion)
              )}
            </ui-suggestion-group>
          ))}
        </ui-autosuggest>
      </Host>
    );
  }
}
